// Updated src/App.tsx with Generate AI Coding Impact capability
import React, { useState, useEffect } from 'react';
import { 
  Brain, MessageSquare, Zap, Sparkles, ChevronRight, 
  ExternalLink, Workflow, Code, GitBranch, Users, 
  AlertTriangle, Target, DollarSign 
} from 'lucide-react';
import ChatInterface from './components/ChatInterface';
import CapabilitySidebar from './components/CapabilitySidebar';
import IntelligenceDashboard from './components/IntelligenceDashboard';
import DetailedAnalysis from './components/DetailedAnalysis';
import WorkflowsTab from './components/WorkflowsTab';

type ViewType = 'chat' | 'dashboard' | 'capabilities' | 'workflows';

function App() {
  const [activeView, setActiveView] = useState<ViewType>('chat');
  const [selectedCapability, setSelectedCapability] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [chatInput, setChatInput] = useState('');
  const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>([]);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [currentAnalysisData, setCurrentAnalysisData] = useState<any>(null);
  const [dashboardHistory, setDashboardHistory] = useState<any[]>([]);
  const [autoStartWorkflow, setAutoStartWorkflow] = useState(false);

  // Example prompts for each capability - UPDATED WITH NEW CAPABILITY
  const capabilityPrompts = {
    'team-rebalancing': [
      "What happens if we move Sarah from Payments to AI Fraud Detection?",
      "Find a replacement for Alex who's leaving the Authentication team",
      "Simulate moving James to Cloud Migration project",
      "Who should we move to fix the Payments project delays?"
    ],
    'bottleneck-diagnosis': [
      "Why is the Payments project delayed?",
      "Identify blockers in Cloud Migration",
      "Analyze why AI Fraud Detection is slowing down",
      "What's causing the Security Enhancement delays?"
    ],
    'initiative-planner': [
      "Plan a new AI integration project",
      "Assemble team for mobile app redesign",
      "Start a new machine learning initiative",
      "Plan cross-platform development team"
    ],
    'budget-simulator': [
      "Simulate 10% budget cut on Engineering",
      "What if we increase Cloud budget by 20%?",
      "Reallocate marketing budget to product",
      "Simulate hiring freeze impact on projects"
    ],
    'ai-coding-impact': [  // NEW CAPABILITY PROMPTS
      "What's the productivity impact of AI coding tools?",
      "Compare project metrics with and without AI assistants",
      "Analyze token usage vs. code quality trade-offs",
      "Should we expand AI tool licenses to more teams?",
      "What's the ROI on our AI coding assistant investment?",
      "How does AI impact developer productivity by skill level?"
    ]
  };

  // Initialize suggested prompts
  useEffect(() => {
    const allPrompts = Object.values(capabilityPrompts).flat();
    setSuggestedPrompts(allPrompts.slice(0, 6));
  }, []);

  // Handle capability selection from sidebar
  const handleCapabilitySelect = (capabilityId: string) => {
    setSelectedCapability(capabilityId);
    setActiveView('chat');
    
    // Set suggested prompts for this capability
    if (capabilityPrompts[capabilityId as keyof typeof capabilityPrompts]) {
      setSuggestedPrompts(capabilityPrompts[capabilityId as keyof typeof capabilityPrompts]);
    }
  };

  // Handle prompt selection
  const handlePromptSelect = (prompt: string) => {
    setChatInput(prompt);
    // Auto-detect capability from prompt
    detectCapabilityFromPrompt(prompt);
  };

  // Detect capability from user input - UPDATED WITH NEW DETECTION
  const detectCapabilityFromPrompt = (prompt: string) => {
    const lowerPrompt = prompt.toLowerCase();
    
    if (lowerPrompt.includes('move') || lowerPrompt.includes('rebalance') || lowerPrompt.includes('replace')) {
      setSelectedCapability('team-rebalancing');
    } else if (lowerPrompt.includes('delay') || lowerPrompt.includes('block') || lowerPrompt.includes('bottleneck')) {
      setSelectedCapability('bottleneck-diagnosis');
    } else if (lowerPrompt.includes('plan') || lowerPrompt.includes('initiative') || lowerPrompt.includes('assemble')) {
      setSelectedCapability('initiative-planner');
    } else if (lowerPrompt.includes('budget') || lowerPrompt.includes('cost') || lowerPrompt.includes('financial')) {
      setSelectedCapability('budget-simulator');
    } else if (lowerPrompt.includes('ai coding') || 
               lowerPrompt.includes('copilot') || 
               lowerPrompt.includes('assistant impact') ||
               lowerPrompt.includes('token usage') ||
               lowerPrompt.includes('productivity impact') ||
               (lowerPrompt.includes('code quality') && lowerPrompt.includes('ai')) ||
               lowerPrompt.includes('roi') && lowerPrompt.includes('ai') ||
               lowerPrompt.includes('generative ai')) {
      setSelectedCapability('ai-coding-impact');
    }
  };

  // Handle dashboard item click
  const handleDashboardItemClick = (item: any) => {
    setActiveView('chat');
    
    // Set the chat input based on the dashboard item
    if (item && item.message) {
      setChatInput(item.message);
      
      // Auto-detect capability
      detectCapabilityFromPrompt(item.message);
      
      // If the item has a specific capability, select it
      if (item.data && item.data.capability) {
        setSelectedCapability(item.data.capability);
      }
    }
    
    // Add to dashboard history
    setDashboardHistory(prev => [item, ...prev.slice(0, 9)]);
  };

  // Handle view detailed analysis
  const handleViewDetailedAnalysis = (analysisData: any) => {
    setCurrentAnalysisData(analysisData);
    setShowDetailedAnalysis(true);
    setAutoStartWorkflow(false);
  };

  // Handle run workflow simulation
  const handleRunWorkflowSimulation = (analysisData: any) => {
    setCurrentAnalysisData(analysisData);
    setShowDetailedAnalysis(true);
    setAutoStartWorkflow(true);
  };

  // Navigation items - Updated to include Workflows
  const navItems = [
    { id: 'chat', label: 'AI Chat', icon: MessageSquare, active: activeView === 'chat' },
    { id: 'dashboard', label: 'Dashboard', icon: Brain, active: activeView === 'dashboard' },
    { id: 'capabilities', label: 'Capabilities', icon: Zap, active: activeView === 'capabilities' },
    { id: 'workflows', label: 'Workflows', icon: Workflow, active: activeView === 'workflows' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-6 h-6 flex flex-col justify-center space-y-1">
                  <div className="h-0.5 w-6 bg-gray-600"></div>
                  <div className="h-0.5 w-6 bg-gray-600"></div>
                  <div className="h-0.5 w-6 bg-gray-600"></div>
                </div>
              </button>
              
              <div className="flex items-center space-x-3">
                {/* Updated: Properly centered local PNG icon */}
                <div className="relative flex items-center justify-center w-16 h-16">
                  <div className="absolute inset-0 flex items-center justify-center overflow-visible">
                    <img 
                      src="/notchup-assets-icon.png" 
                      alt="AI Chief of Staff Logo" 
                      className="w-24 h-24 object-contain"
                    />
                  </div>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">AI Chief of Staff</h1>
                  <p className="text-sm text-gray-600">Organizational Intelligence Assistant</p>
                </div>
              </div>
            </div>

            <nav className="hidden md:flex items-center space-x-2 bg-gray-100 p-1 rounded-lg">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveView(item.id as ViewType)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                      item.active
                        ? 'bg-white shadow-sm text-blue-600 border border-blue-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="flex items-center space-x-3">
              <div className="hidden md:flex items-center space-x-2 text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>AI Online</span>
              </div>
              {dashboardHistory.length > 0 && (
                <div className="relative group">
                  <button className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                    <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {dashboardHistory.length}
                    </div>
                  </button>
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-3 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 z-10">
                    <p className="text-sm font-medium text-gray-900 mb-2">Recent Actions</p>
                    {dashboardHistory.slice(0, 3).map((item, index) => (
                      <div key={index} className="text-xs text-gray-600 py-1 border-b border-gray-100 last:border-0">
                        {item.type}: {item.data?.name || item.message?.substring(0, 40)}...
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <div className="md:hidden border-b bg-white shadow-sm">
        <div className="flex justify-around py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id as ViewType)}
                className={`flex flex-col items-center p-2 transition-colors ${
                  item.active 
                    ? 'text-blue-600 bg-blue-50 rounded-lg' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon size={20} />
                <span className="text-xs mt-1">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {isSidebarOpen && (
          <div className="w-64 lg:w-80 border-r bg-white overflow-y-auto shadow-sm">
            <CapabilitySidebar
              onSelectCapability={handleCapabilitySelect}
              selectedCapability={selectedCapability}
            />
          </div>
        )}

        {/* Main Area */}
        <div className={`flex-1 overflow-hidden ${isSidebarOpen ? '' : 'mx-auto max-w-6xl'}`}>
          {activeView === 'chat' && (
            <div className="h-full flex flex-col">
              {/* Chat Interface */}
              <div className="flex-1 overflow-hidden">
                <ChatInterface 
                  selectedCapability={selectedCapability}
                  onPromptSelect={handlePromptSelect}
                  initialInput={chatInput}
                  onInputChange={setChatInput}
                  onViewDetailedAnalysis={handleViewDetailedAnalysis}
                />
              </div>
            </div>
          )}

          {activeView === 'dashboard' && (
            <IntelligenceDashboard 
              onSelectCapability={handleCapabilitySelect}
              onDashboardItemClick={handleDashboardItemClick}
            />
          )}

          {activeView === 'capabilities' && (
            <div className="p-6 h-full overflow-y-auto">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Intelligence Capabilities</h2>
                <p className="text-gray-600 mb-8">Explore all available intelligence features</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    {
                      id: 'team-rebalancing',
                      title: 'Generate Capacity Plan',
                      description: 'Predict impact of personnel changes before making moves',
                      color: 'from-blue-500 to-cyan-500',
                      example: "What happens if we move Sarah to AI Fraud Detection?",
                      stats: 'Used 42 times • 92% accuracy',
                      icon: '👥'
                    },
                    {
                      id: 'bottleneck-diagnosis',
                      title: 'Automate Root Cause Analysis',
                      description: 'Identify root causes of delays before they escalate',
                      color: 'from-orange-500 to-red-500',
                      example: "Why is the Payments project delayed?",
                      stats: 'Used 28 times • 88% accuracy',
                      icon: '⚠️'
                    },
                    {
                      id: 'initiative-planner',
                      title: 'Match Internal Talent',
                      description: 'Optimally plan and staff new strategic initiatives',
                      color: 'from-green-500 to-emerald-500',
                      example: "Plan a new AI integration project",
                      stats: 'Used 15 times • 85% accuracy',
                      icon: '🎯'
                    },
                    {
                      id: 'budget-simulator',
                      title: 'Optimize IT Budget',
                      description: 'Simulate financial impact of budget changes',
                      color: 'from-purple-500 to-pink-500',
                      example: "Simulate 10% budget cut on Engineering",
                      stats: 'Used 23 times • 90% accuracy',
                      icon: '💰'
                    },
                    {
                      id: 'ai-coding-impact',
                      title: 'Generate AI Coding Impact',  // UPDATED NAME
                      description: 'Measure productivity & ROI of AI coding tools',
                      color: 'from-indigo-500 to-purple-500',
                      example: "What's the ROI on our AI coding tools?",
                      stats: 'Used 18 times • 87% accuracy',
                      icon: '🤖'
                    }
                  ].map((cap) => (
                    <div
                      key={cap.id}
                      className="bg-white rounded-xl border border-gray-200 p-6 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                      onClick={() => {
                        handleCapabilitySelect(cap.id);
                        setChatInput(cap.example);
                      }}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`p-4 rounded-xl bg-gradient-to-r ${cap.color} shadow-sm group-hover:scale-105 transition-transform`}>
                          <span className="text-2xl">{cap.icon}</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <h3 className="font-bold text-gray-900 text-lg">{cap.title}</h3>
                            <div className="p-1 bg-gray-100 rounded-lg group-hover:bg-blue-50 transition-colors">
                              <ChevronRight size={16} className="text-gray-500 group-hover:text-blue-500" />
                            </div>
                          </div>
                          <p className="text-gray-600 mt-1">{cap.description}</p>
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg group-hover:bg-blue-50 transition-colors">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm text-gray-500">Try asking:</p>
                              <Sparkles size={14} className="text-yellow-500" />
                            </div>
                            <p className="text-gray-900 font-medium">"{cap.example}"</p>
                          </div>
                          <div className="mt-3 text-xs text-gray-500">
                            {cap.stats}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Capabilities Overview */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">How It Works</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-xl border border-blue-100">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                        <MessageSquare size={24} className="text-blue-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">1. Ask Questions Naturally</h4>
                      <p className="text-sm text-gray-600">Use natural language to ask about team dynamics, project health, or financial scenarios.</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-white p-6 rounded-xl border border-green-100">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                        <Brain size={24} className="text-green-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">2. AI Analysis</h4>
                      <p className="text-sm text-gray-600">Our AI analyzes historical data, patterns, and correlations to provide intelligent insights.</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                        <ExternalLink size={24} className="text-purple-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">3. Detailed Visualizations</h4>
                      <p className="text-sm text-gray-600">Explore comprehensive charts, metrics, and actionable recommendations.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeView === 'workflows' && (
            <WorkflowsTab />
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4 px-6 text-center text-sm text-gray-500">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            <div className="flex items-center space-x-4">
              <span>AI Chief of Staff v1.0</span>
              <span className="hidden md:inline">•</span>
              <span>Powered by Intelligence Algorithms</span>
            </div>
            <div className="flex items-center space-x-4">
              <a href="#" className="hover:text-blue-600 transition-colors">Privacy</a>
              <span>•</span>
              <a href="#" className="hover:text-blue-600 transition-colors">Terms</a>
              <span>•</span>
              <span>© {new Date().getFullYear()} AI Chief of Staff</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Detailed Analysis Modal */}
      {showDetailedAnalysis && currentAnalysisData && (
        <DetailedAnalysis 
          analysisData={currentAnalysisData}
          capability={selectedCapability || 'general'}
          onClose={() => {
            setShowDetailedAnalysis(false);
            setCurrentAnalysisData(null);
            setAutoStartWorkflow(false);
          }}
          autoStartWorkflow={autoStartWorkflow}
        />
      )}
    </div>
  );
}

export default App;